import styles from '@/pages/app/Settings/styles.module.scss'

import InfoIcon from '@/assets/icons/info.svg?react'
import ConfirmDialog from '@/components/ConfirmDialog'
import { Dropdown, DropdownItem } from '@/components/Dropdown'
import Tooltip from '@/components/Tooltip'
import { useHasDepartments } from '@/hooks/useHasDepartments'
import PermissionTooltip from '@/pages/app/Settings/PermissionTooltip'
import { NoteSharingAudience } from '@/types'

// Keep helperText short: Dropdown menus size to `width: max-content`, so long copy widens them.
// Ordered most to least restrictive, matching the other org controls.
const getOptions = (noun: string) => [
  {
    id: NoteSharingAudience.Owner,
    title: 'Disabled',
    helperText: 'Sharing of meeting notes or transcripts is blocked',
  },
  {
    id: NoteSharingAudience.Internal,
    title: 'Internal only',
    helperText: `Members may share only with people in the ${noun}. Public links are off.`,
  },
  {
    id: NoteSharingAudience.External,
    title: 'Internal and external',
    helperText: `Members may share with specific people outside the ${noun}. Public links are off.`,
  },
  {
    id: NoteSharingAudience.Public,
    title: 'Public links',
    helperText: `Members may share inside and outside the ${noun}, including public links`,
  },
]

const getDropdownItems = (current: NoteSharingAudience, noun: string): DropdownItem[] =>
  getOptions(noun).map((option) => ({ ...option, selected: option.id === current }))

type Props = {
  audience: NoteSharingAudience
  isAdmin: boolean
  isSaving: boolean
  /** A sibling note-sharing save is in flight. Blocks the dropdown without claiming this one is
   *  saving — the confirm dialog's spinner stays tied to `isSaving`. */
  siblingSaving?: boolean
  pendingBlock: boolean
  onSelect: (audience: NoteSharingAudience) => void
  onConfirmBlock: () => void
  onCancelBlock: () => void
}

/**
 * Org-wide meeting-note sharing audience picker.
 *
 * Items are derived from `audience` on every render rather than held in local state, so the
 * dropdown can't drift from the server value and needs no resync effect or manual rollback.
 */
export default function NoteSharingAudienceSection({
  audience,
  isAdmin,
  isSaving,
  siblingSaving = false,
  pendingBlock,
  onSelect,
  onConfirmBlock,
  onCancelBlock,
}: Props) {
  const noun = useHasDepartments() ? 'organization' : 'team'

  const handleSelect = (selectedItems: DropdownItem[]) => {
    if (selectedItems.length === 0) return
    onSelect(selectedItems[0].id as NoteSharingAudience)
  }

  return (
    <>
      <div className={styles.settingsItem} data-testid="note-sharing-setting">
        <div className={styles.toggleLabel}>
          Note sharing
          <Tooltip text={`Who ${noun} members can share meeting notes with`} placement="top" hideArrow>
            <InfoIcon className={styles.infoIcon} />
          </Tooltip>
        </div>
        <PermissionTooltip>
          <Dropdown
            items={getDropdownItems(audience, noun)}
            onSelect={handleSelect}
            placeholder="Select audience"
            className={styles.localDataPolicyDropdown}
            disabled={!isAdmin || isSaving || siblingSaving || pendingBlock}
            ariaLabel="Note sharing audience"
          />
        </PermissionTooltip>
      </div>
      <ConfirmDialog
        isOpen={pendingBlock}
        confirmType="destructive"
        isConfirming={isSaving}
        title="Disable note sharing?"
        message="Members won't be able to share meeting notes, and existing share links will stop working."
        warning="Turning note sharing back on restores every existing link — nothing is deleted."
        confirmLabel="Disable"
        onConfirm={onConfirmBlock}
        onCancel={onCancelBlock}
      />
    </>
  )
}
